import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Coins, Gift, LogOut, Languages, Volume2, VolumeX, Shield, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import AnimatedNumber from "./AnimatedNumber";
import { avatarUrl } from "../lib/format";
import { isMuted, setMuted, playClick } from "../lib/sound";
import { api } from "../lib/api";
import NotificationBell from "./NotificationBell";

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLang();
  const nav = useNavigate();
  const loc = useLocation();
  const [muted, setMutedState] = useState(isMuted());
  const [unread, setUnread] = useState(0);

  const isAdmin = user?.role === "admin";
  useEffect(() => {
    if (!user) return;
    const url = isAdmin ? "/admin/support/unread" : "/support/unread";
    const poll = () => api.get(url).then(({ data }) => setUnread(data.count || 0)).catch(() => {});
    poll();
    const id = setInterval(poll, 8000);
    return () => clearInterval(id);
  }, [user, isAdmin, loc.pathname]);

  const toggleSound = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) playClick();
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070B1F]/85 border-b border-amber-500/20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-1.5">
        <Link to="/app" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          <img src="/logo.png" alt="Bam Bam Bhole" className="h-9 w-9 rounded-xl object-cover ring-1 ring-amber-500/40" />
          <span className="hidden sm:inline font-display text-xl sm:text-2xl font-extrabold gold-text tracking-tight">
            {t("brand")}
          </span>
        </Link>

        <div className="flex items-center gap-1 sm:gap-3">
          {user && (
            <div
              key={user.coins}
              className="flex items-center gap-1 sm:gap-1.5 rounded-full border border-amber-500/40 bg-black/40 px-2 sm:px-3 py-1 sm:py-1.5 animate-pulse-glow"
              data-testid="user-wallet-balance"
            >
              <Coins size={17} className="text-amber-400" />
              <span className="font-mono-num font-bold text-amber-200 text-sm sm:text-base">₹</span>
              <AnimatedNumber value={user.coins} className="font-mono-num font-bold text-amber-200 text-sm sm:text-base" />
            </div>
          )}

          <button
            onClick={toggleSound}
            data-testid="sound-toggle-btn"
            title={t("sound")}
            className={`grid place-items-center h-8 w-8 sm:h-9 sm:w-9 rounded-full border bg-black/30 transition-colors ${muted ? "border-slate-600 text-slate-400" : "border-cyan-400/40 text-cyan-300"}`}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>

          <button
            onClick={toggle}
            data-testid="lang-toggle-btn"
            className="flex items-center gap-1 rounded-full border border-cyan-400/30 bg-black/30 px-2 py-1 sm:px-2.5 sm:py-1.5 text-xs font-bold text-cyan-300 hover:border-cyan-400/70 transition-colors"
          >
            <Languages size={15} />
            {lang === "hi" ? "EN" : "हि"}
          </button>

          {user && (
            <>
              {user.role !== "admin" && <NotificationBell />}
              <button
                onClick={() => nav("/app/payments")}
                data-testid="header-payments-btn"
                title={t("payments")}
                className="hidden sm:grid place-items-center h-9 px-3 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs font-bold hover:border-emerald-400/80 transition-colors"
              >
                ₹ {t("addMoney")}
              </button>
              <button
                onClick={() => nav(isAdmin ? "/admin/dashboard" : "/app/support")}
                data-testid="header-support-btn"
                title={t("support")}
                className="relative grid place-items-center h-8 w-8 sm:h-9 sm:w-9 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 hover:border-cyan-400 transition-colors"
              >
                <MessageCircle size={16} />
                {unread > 0 && (
                  <span data-testid="support-unread-badge" className="absolute -top-1 -right-1 grid place-items-center h-4 min-w-4 px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
              {user.role === "admin" && (
                <button
                  onClick={() => nav("/admin/dashboard")}
                  data-testid="header-admin-btn"
                  title={t("adminPanel")}
                  className="grid place-items-center h-8 w-8 sm:h-9 sm:w-9 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 hover:border-amber-400 transition-colors"
                >
                  <Shield size={16} />
                </button>
              )}
              <button
                onClick={() => nav("/app/profile")}
                data-testid="header-profile-btn"
                title={t("profile")}
                className="grid place-items-center h-8 w-8 sm:h-9 sm:w-9 rounded-full border border-amber-500/40 bg-gradient-to-br from-amber-400/30 to-purple-600/20 font-display font-bold text-sm overflow-hidden"
              >
                {user.picture ? <img src={avatarUrl(user.picture)} alt="" className="h-full w-full object-cover" /> : (user.name?.[0] || "?").toUpperCase()}
              </button>
              <button
                onClick={() => { logout(); nav("/"); }}
                data-testid="logout-btn"
                className="grid place-items-center h-8 w-8 sm:h-9 sm:w-9 rounded-full border border-rose-500/30 bg-black/30 text-rose-300 hover:border-rose-500/70 transition-colors"
                title={t("logout")}
              >
                <LogOut size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
